import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Nav from "../../components/Home/Nav";
import Footer from "../../components/Home/Footer";
import {
  HiUser,
  HiMail,
  HiPhone,
  HiLockClosed,
  HiEye,
  HiEyeOff,
  HiArrowRight,
  HiShieldCheck,
  HiRefresh,
} from "react-icons/hi";
import { GoogleLogin } from "@react-oauth/google";
import Cookies from "js-cookie";

function Register() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    confPassword: "",
  });
  const [otpCode, setOtpCode] = useState("");
  const [requireOtp, setRequireOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // 60-second Resend Timer
  useEffect(() => {
    let timer;
    if (requireOtp && resendCooldown > 0) {
      setCanResend(false);
      timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    } else if (resendCooldown === 0) {
      setCanResend(true);
    }
    return () => clearInterval(timer);
  }, [requireOtp, resendCooldown]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/api/resend-register-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: formData.email }),
        }
      );
      const data = await response.json();
      if (response.ok) {
        toast.success(data.msg || "A new 6-digit verification code has been sent!");
        setResendCooldown(60);
      } else {
        toast.error(data.msg || "Failed to resend code");
      }
    } catch (err) {
      toast.error("Network error. Could not resend verification code.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegistration = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!requireOtp) {
      if (!formData.name.trim()) newErrors.name = "Name is required";
      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Invalid email address";
      }

      if (!formData.phone.trim()) {
        newErrors.phone = "Phone number is required";
      } else if (!/^\d{10}$/.test(formData.phone)) {
        newErrors.phone = "Invalid phone number";
      }

      if (!formData.password.trim()) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 8) {
        newErrors.password = "Minimum 8 characters";
      } else if (
        !/(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])/.test(formData.password)
      ) {
        newErrors.password = "Need uppercase, number & symbol";
      }

      if (formData.password !== formData.confPassword) {
        newErrors.confPassword = "Passwords do not match";
      }
    } else {
      if (!otpCode.trim()) {
        newErrors.otp = "6-digit verification code is required";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const payload = requireOtp
        ? { ...formData, otp: otpCode }
        : formData;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || `${process.env.REACT_APP_API_URL || "http://localhost:5000"}`}/api/register`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (response.ok) {
        if (data.requireOtp) {
          setRequireOtp(true);
          setResendCooldown(data.cooldownSeconds || 60);
          toast.info(data.msg || "6-digit verification code sent to your email.");
          return;
        }

        toast.success(data.msg || "Account verified & created successfully!");
        navigate("/login");
      } else {
        toast.error(data.msg || "Registration failed");
      }
    } catch (error) {
      toast.error("Connection error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/api/google-auth`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ credential: credentialResponse.credential }),
        }
      );

      if (response.ok) {
        const { accessToken, roleId, roleName, userId } = await response.json();
        Cookies.set("accessToken", accessToken, { expires: 30 / 1440 });
        Cookies.set("roleId", roleId, { expires: 30 / 1440 });
        Cookies.set("roleName", roleName, { expires: 30 / 1440 });
        Cookies.set("userId", userId, { expires: 30 / 1440 });

        toast.success("Account ready! Welcome.");
        navigate(roleName === "Admin" || roleName === "Super Admin" ? "/admin/dashboard" : "/");
      } else {
        const errorData = await response.json();
        toast.error(errorData.msg || "Google authentication failed");
      }
    } catch (error) {
      toast.error("Connection error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Nav />

      <main className="flex-grow flex items-center justify-center p-4 pt-32 pb-20">
        <div className="w-full max-w-[550px] animate-in fade-in zoom-in duration-500">
          <div className="glass rounded-3xl p-8 md:p-10 shadow-2xl relative overflow-hidden">
            {/* Background Decoration */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-blue-600/10 rounded-full blur-3xl"></div>

            <div className="relative z-10">
              <div className="text-center mb-8">
                {requireOtp ? (
                  <>
                    <span className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-[10px] font-black uppercase tracking-widest mb-2 border border-blue-100">
                      Email Verification Required
                    </span>
                    <h1 className="text-2xl font-bold text-slate-900 mb-1">
                      Enter 6-Digit OTP Code
                    </h1>
                    <p className="text-slate-500 text-xs">
                      We sent a verification code to <strong>{formData.email}</strong>. Valid for 10 minutes.
                    </p>
                  </>
                ) : (
                  <>
                    <h1 className="text-3xl font-bold text-slate-900 mb-2">
                      Create Account
                    </h1>
                    <p className="text-slate-500">Join S.D Enterprises today</p>
                  </>
                )}
              </div>

              <form onSubmit={handleRegistration} className="space-y-5">
                {!requireOtp ? (
                  <>
                    {/* Name Field */}
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-slate-700 ml-1">
                        Full Name
                      </label>
                      <div className="relative group">
                        <HiUser className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors text-xl" />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Enter your full name"
                          className={`input-field pl-12 ${errors.name ? "border-red-500 focus:ring-red-500/20" : ""}`}
                          autoFocus
                        />
                      </div>
                      {errors.name && (
                        <p className="text-xs text-red-500 font-medium ml-1">
                          {errors.name}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {/* Email Field */}
                      <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-slate-700 ml-1">
                          Email
                        </label>
                        <div className="relative group">
                          <HiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors text-xl" />
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="Enter your email"
                            className={`input-field pl-12 ${errors.email ? "border-red-500 focus:ring-red-500/20" : ""}`}
                          />
                        </div>
                        {errors.email && (
                          <p className="text-xs text-red-500 font-medium ml-1">
                            {errors.email}
                          </p>
                        )}
                      </div>

                      {/* Phone Field */}
                      <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-slate-700 ml-1">
                          Phone Number
                        </label>
                        <div className="relative group">
                          <HiPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors text-xl" />
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="98XXXXXXXX"
                            className={`input-field pl-12 ${errors.phone ? "border-red-500 focus:ring-red-500/20" : ""}`}
                          />
                        </div>
                        {errors.phone && (
                          <p className="text-xs text-red-500 font-medium ml-1">
                            {errors.phone}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Password Field */}
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-slate-700 ml-1">
                        Password
                      </label>
                      <div className="relative group">
                        <HiLockClosed className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors text-xl" />
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="••••••••"
                          className={`input-field pl-12 pr-12 ${errors.password ? "border-red-500 focus:ring-red-500/20" : ""}`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          {showPassword ? (
                            <HiEyeOff size={20} />
                          ) : (
                            <HiEye size={20} />
                          )}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="text-xs text-red-500 font-medium ml-1">
                          {errors.password}
                        </p>
                      )}
                    </div>

                    {/* Confirm Password Field */}
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-slate-700 ml-1">
                        Confirm Password
                      </label>
                      <div className="relative group">
                        <HiLockClosed className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors text-xl" />
                        <input
                          type={showPassword ? "text" : "password"}
                          name="confPassword"
                          value={formData.confPassword}
                          onChange={handleInputChange}
                          placeholder="••••••••"
                          className={`input-field pl-12 ${errors.confPassword ? "border-red-500 focus:ring-red-500/20" : ""}`}
                        />
                      </div>
                      {errors.confPassword && (
                        <p className="text-xs text-red-500 font-medium ml-1">
                          {errors.confPassword}
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  /* 6-Digit OTP Field */
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-700 ml-1">
                        6-Digit Verification Code
                      </label>
                      <div className="relative group">
                        <HiShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-500 text-2xl" />
                        <input
                          type="text"
                          maxLength="6"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value)}
                          placeholder="000000"
                          className="input-field pl-12 tracking-[8px] font-mono font-bold text-xl text-slate-800"
                          autoFocus
                        />
                      </div>
                      {errors.otp && (
                        <p className="text-xs text-red-500 font-medium ml-1">
                          {errors.otp}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center justify-between text-xs pt-1">
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={!canResend || isLoading}
                        className={`font-bold flex items-center gap-1 transition-colors ${
                          canResend
                            ? "text-blue-600 hover:text-blue-800 cursor-pointer"
                            : "text-slate-400 cursor-not-allowed"
                        }`}
                      >
                        <HiRefresh size={14} className={isLoading ? "animate-spin" : ""} />
                        {canResend ? "Resend Verification Code" : `Resend code in ${resendCooldown}s`}
                      </button>

                      <button
                        type="button"
                        onClick={() => setRequireOtp(false)}
                        className="text-slate-500 font-medium hover:text-slate-700"
                      >
                        Edit Registration Info
                      </button>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full py-3.5 mt-4 flex items-center justify-center gap-2 group relative overflow-hidden disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <span>{requireOtp ? "Verify OTP & Complete Account" : "Create Account"}</span>
                      <HiArrowRight className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>

              {!requireOtp && (
                <>
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-200"></div>
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white/80 px-2 text-slate-500 font-semibold backdrop-blur-sm">
                        Or sign up with
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-center w-full">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => toast.error("Google Sign-Up failed")}
                      useOneTap
                    />
                  </div>
                </>
              )}

              <div className="mt-8 pt-6 border-t border-slate-200/60 text-center">
                <p className="text-sm text-slate-500">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="font-bold text-blue-600 hover:text-blue-700 ml-1"
                  >
                    Log in here
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Register;
